import { Button, Flex, Group, Stack, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { ProgrammeDefaultsCategoryKey } from '@dealstack/shared/lib/generated';
import {
	useTranslationMipManage,
	useTranslationOrganization,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	CATEGORY_LABEL_KEYS,
	isOrganizationNamespacedLabel,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/ProgrammeDefaultsBanner/programmeDefaultsBanner.utils';
import { useProgrammeDefaultsBanner } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/ProgrammeDefaultsBanner/useProgrammeDefaultsBanner';
import { StyledBanner } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/ProgrammeDefaultsBanner/styles';

interface Props {
	dealId: string;
	mipId: string | undefined;
}

export const ProgrammeDefaultsBanner = ({ dealId, mipId }: Props) => {
	const tOrganization = useTranslationOrganization();
	const tMipManage = useTranslationMipManage();
	const { summary, isApplying, onApply, onDismiss } = useProgrammeDefaultsBanner(dealId, mipId);

	if (!summary) {
		return null;
	}

	const categoryLabel = (category: ProgrammeDefaultsCategoryKey) =>
		isOrganizationNamespacedLabel(category)
			? tOrganization(CATEGORY_LABEL_KEYS[category])
			: tMipManage(CATEGORY_LABEL_KEYS[category]);

	const configured = summary.categories.filter(({ configured: isSet }) => isSet);

	return (
		<StyledBanner>
			<Flex gap="0.75rem" align="flex-start">
				<Icon variant="plant-grown" size="vlg" color="blue.6" />
				<Stack gap="0.25rem" flex={1}>
					<Text size="B5Bold">{tOrganization('programme_defaults.confirm.title')}</Text>
					<Text size="B6Medium" c="gray.9">
						{tOrganization('programme_defaults.confirm.description', {
							organizationName: summary.organizationName,
						})}
					</Text>
					<Text size="B6Medium" c="gray.9">
						{configured.map(({ category }) => categoryLabel(category)).join(' · ')}
					</Text>
				</Stack>
				<Group gap="xs">
					<Button variant="default" onClick={onDismiss} disabled={isApplying}>
						{tOrganization('programme_defaults.confirm.start_blank')}
					</Button>
					<Button onClick={() => onApply()} loading={isApplying}>
						{tOrganization('programme_defaults.confirm.use_defaults')}
					</Button>
				</Group>
			</Flex>
		</StyledBanner>
	);
};
